import React, { PureComponent, ReactNode, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { shortTypes as assignmentTypes } from './assignmentTypes';
import permissionTypes from './permissionTypes';

import checkAccessBasedOnAssignmentPermissions from './checkAccessBasedOnAssignmentPermissions.helper';
import getPermissionsFromTree from './getPermissionsFromTree.helper';

type Permission = {
  permissionType: string,
  permissionPath: string,
}

type Assignment = {
  assignmentType: string,
  requiredPermission: string,
}

type User = {
  permissions: Object,
}

type AuthorizeUserProps = {
  withPermissions: Permission[],
  user: User,
  withAssignments?: Assignment[],
  children?: ReactNode,
  otherwise?: ReactNode,
  companyCode?: string | null,
  className?: string,
  requireCompanyCode?: boolean,
  requireBothPermissionsAndAssignments?: boolean,
}

class AuthorizeUser extends PureComponent<AuthorizeUserProps> {
  static propTypes = {
    user: PropTypes.shape({
      permissions: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    }).isRequired,
    withPermissions: PropTypes.arrayOf(PropTypes.shape({
      permissionType: PropTypes.oneOf(Object.values(permissionTypes)).isRequired,
      permissionPath: PropTypes.string.isRequired,
    })).isRequired,
    withAssignments: PropTypes.arrayOf(PropTypes.shape({
      assignmentType: PropTypes.oneOf(Object.values(assignmentTypes)).isRequired,
      requiredPermission: PropTypes.string.isRequired,
    })),
    children: PropTypes.node,
    otherwise: PropTypes.node,
    companyCode: PropTypes.string,
    requireCompanyCode: PropTypes.bool,
    className: PropTypes.string,
    requireBothPermissionsAndAssignments: PropTypes.bool,
  };

  static defaultProps = {
    withPermissions: [],
    withAssignments: [],
    companyCode: null,
    requireCompanyCode: true,
    requireBothPermissionsAndAssignments: true,
  };

  render() {
    const {
      user,
      withPermissions,
      withAssignments,
      otherwise,
      companyCode,
      requireCompanyCode,
      className,
      requireBothPermissionsAndAssignments,
    } = this.props;

    const hasAccessBasedOnPermissions = !withPermissions.length ? true : withPermissions.some(
      ({ permissionPath, permissionType }) => {
        const permission = getPermissionsFromTree(user.permissions, permissionPath);
        return permission[permissionType];
      }
    );

    const hasAccessBasedOnAssignments = checkAccessBasedOnAssignmentPermissions({
      user,
      withAssignments,
      companyCode,
      requireCompanyCode,
    });

    const hasAccess = requireBothPermissionsAndAssignments
      ? hasAccessBasedOnPermissions && hasAccessBasedOnAssignments
      : hasAccessBasedOnPermissions || hasAccessBasedOnAssignments;

    if (hasAccess) {
      return isValidElement(this.props.children)
        ? React.cloneElement(this.props.children, { className })
        : null;
    }

    if (otherwise) {
      return otherwise;
    }

    return null;
  }
}

export default withRouter<any, any>(connect(
  ({ user, shared }: any) => ({
    user: user.user,
    companyCode: shared.companyCode,
  })
)(AuthorizeUser as any));
